import {
  Configuration,
  LogLevel,
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from '@azure/msal-browser';

// Check if Azure AD is configured
export const isAzureAdConfigured =
  !!import.meta.env.VITE_AZURE_AD_CLIENT_ID && !!import.meta.env.VITE_AZURE_AD_TENANT_ID;

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || 'not-configured',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_AZURE_AD_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // Use sessionStorage to avoid MSAL v4+ localStorage encryption complexity
    // - No encryption key cookie that can be lost
    // - Works reliably in incognito/private mode
    // - Trade-off: No cross-tab auth sharing, re-login needed after browser restart
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          case LogLevel.Info:
            if (import.meta.env.DEV) console.info('[MSAL]', message);
            break;
          case LogLevel.Verbose:
            if (import.meta.env.DEV) console.debug('[MSAL]', message);
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Error,
    },
  },
};

// Create the MSAL instance (initialization happens in main.tsx before render)
export const msalInstance = new PublicClientApplication(msalConfig);

// Register event callbacks for automatic account management
// This runs BEFORE initialize() is called, which is fine - events will fire after init
if (isAzureAdConfigured) {
  msalInstance.addEventCallback((event: EventMessage) => {
    // Set active account on successful login
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const result = event.payload as AuthenticationResult;
      if (result?.account) {
        console.log('[MSAL] Login success, setting active account:', result.account.username);
        msalInstance.setActiveAccount(result.account);
      }
    }

    // Handle successful token acquisition (silent refresh)
    if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      const result = event.payload as AuthenticationResult;
      if (result?.account && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(result.account);
      }
    }

    // Clear active account on logout
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      console.log('[MSAL] Logout success, clearing active account');
      msalInstance.setActiveAccount(null);
    }

    // Log failures for debugging (check error property on any event)
    if (event.error) {
      console.warn('[MSAL] Event error:', event.eventType, event.error);
    }
  });
}

export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/**
 * Initialize MSAL instance. Must be called before using any MSAL APIs.
 * MsalProvider will handle handleRedirectPromise() internally.
 */
export async function initializeMsal(): Promise<void> {
  if (!isAzureAdConfigured) {
    console.warn('[MSAL] Azure AD not configured - SSO disabled');
    return;
  }

  try {
    // Initialize the MSAL instance
    await msalInstance.initialize();
    console.log('[MSAL] Initialized successfully');

    // Set active account if one exists (returning user with valid session)
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // Prefer the account that was previously active, or use the first one
      const activeAccount = msalInstance.getActiveAccount() || accounts[0];
      msalInstance.setActiveAccount(activeAccount);
      console.log('[MSAL] Active account set:', activeAccount.username);
    }
  } catch (error) {
    console.error('[MSAL] Initialization failed:', error);
    throw error;
  }
}
